import { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Button } from './ui';

/** Shrinks a chosen image to a small data URL, so a logo is cheap to store and to print. */
export async function logoDataUrl(file: File, maxW = 420, maxH = 180): Promise<string> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error('Choose a PNG, JPEG or WebP image.');
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const png = canvas.toDataURL('image/png');
  if (png.length <= 300_000) return png;
  // Photographic logos compress far better as JPEG; flatten transparency onto white.
  const flat = document.createElement('canvas');
  flat.width = canvas.width; flat.height = canvas.height;
  const ctx = flat.getContext('2d')!;
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, flat.width, flat.height); ctx.drawImage(canvas, 0, 0);
  return flat.toDataURL('image/jpeg', 0.85);
}

/** value: current logo (data URL) or null. onChange gets a new data URL, or null to remove. */
export function LogoField({ value, onChange, hint }: { value: string | null | undefined; onChange: (v: string | null) => void; hint?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file?: File) => {
    if (!file) return;
    setError(null);
    try { onChange(await logoDataUrl(file)); } catch (e) { setError((e as Error).message); }
    if (ref.current) ref.current.value = '';
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-line-strong">
          {value ? <img src={value} alt="Customer logo" className="max-h-full max-w-full object-contain" />
            : <ImagePlus className="h-5 w-5 text-ink-400" aria-hidden />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" type="button" onClick={() => ref.current?.click()}>{value ? 'Replace' : 'Upload logo'}</Button>
          {value && <Button size="sm" variant="ghost" type="button" icon={<X className="h-4 w-4" />} onClick={() => onChange(null)}>Remove</Button>}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
      </div>
      {(error || hint) && <p className={`mt-1 text-xs ${error ? 'text-danger' : 'text-ink-500'}`}>{error ?? hint}</p>}
    </div>
  );
}
